import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useDispatch } from 'src/redux/store';
import { addGroupParticipants } from 'src/redux/slices/chat';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Textarea } from 'src/@/components/ui/textarea';
import { Label } from 'src/@/components/ui/label';

const phoneClean = (raw) => String(raw || '').replace(/\D/g, '');

export default function AddParticipantsModal({ open, onClose, chatId }) {
  const { t } = useTranslation('chat');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [raw, setRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const waIds = useMemo(() =>
    raw.split(/[\n,;]/).map(phoneClean).filter(Boolean),
    [raw]
  );

  const submit = useCallback(async () => {
    if (!waIds.length) {
      enqueueSnackbar(t('groups.participants.errorEmpty', 'Adicione pelo menos um número'), { variant: 'error' });
      return;
    }
    setSubmitting(true);
    const result = await dispatch(addGroupParticipants(chatId, waIds));
    setSubmitting(false);
    if (result?.success) {
      enqueueSnackbar(t('groups.participants.addedSuccess', 'Participantes adicionados'), { variant: 'success' });
      onClose?.();
    } else {
      enqueueSnackbar(result?.message || t('groups.participants.addedError', 'Erro ao adicionar'), { variant: 'error' });
    }
  }, [waIds, dispatch, chatId, enqueueSnackbar, t, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000] animate-in fade-in duration-150" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative mx-auto mt-20 w-[92%] max-w-md rounded-2xl bg-white dark:bg-neutral-900 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-neutral-800">
          <h3 className="text-base font-semibold">{t('groups.participants.add', 'Adicionar participantes')}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><Iconify icon="eva:close-outline" width={18} /></Button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('groups.participants.numbers', 'Números (com DDI)')}</Label>
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="5511999998888"
            rows={5}
            className="rounded-xl font-mono text-xs"
          />
          <div className="text-[11px] text-gray-500">{t('groups.create.participantsCount', '{{count}} participantes', { count: waIds.length })}</div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-neutral-800 bg-gray-50/60 dark:bg-neutral-900/60">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('groups.create.cancel', 'Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white">
            {submitting ? t('groups.create.submitting', 'Salvando...') : t('groups.participants.add', 'Adicionar')}
          </Button>
        </div>
      </div>
    </div>
  );
}
